import { useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { PushNotifications } from "@capacitor/push-notifications";

// The app can't run code while it is closed or in the background (the OS
// freezes the WebView), so "refreshes in the background" means: the moment
// the app comes back to the front, or a push arrives while it is open, the
// current screen reloads its data on its own, with no pull-down needed.
const MIN_AWAY_MS = 10_000;

function typingInField() {
  const el = document.activeElement;
  return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || (el as HTMLElement).isContentEditable);
}

export function useAutoRefresh(refresh: () => void) {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    let hiddenAt: number | null = document.visibilityState === "hidden" ? Date.now() : null;

    function onReturn() {
      if (hiddenAt && Date.now() - hiddenAt >= MIN_AWAY_MS) refreshRef.current();
      hiddenAt = null;
    }
    function onVisibility() {
      if (document.visibilityState === "hidden") hiddenAt = Date.now();
      else onReturn();
    }
    document.addEventListener("visibilitychange", onVisibility);

    const removers: Array<() => void> = [];
    if (Capacitor.isNativePlatform()) {
      CapApp.addListener("appStateChange", ({ isActive }) => {
        if (isActive) onReturn();
        else hiddenAt = Date.now();
      }).then((h) => removers.push(() => h.remove())).catch(() => {});
      // A push that lands while the app is open: update the screen unless
      // the user is mid-typing (a reload would wipe what they've entered).
      PushNotifications.addListener("pushNotificationReceived", () => {
        if (!typingInField()) refreshRef.current();
      }).then((h) => removers.push(() => h.remove())).catch(() => {});
    }

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      removers.forEach((r) => r());
    };
  }, []);
}
